"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _Pressable = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Pressable"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.linkProps]);

// ensure explicit selection of tokens
const selectStyles = _ref => {
  let {
    backgroundColor,
    outlineColor,
    outlineOffset,
    outlineStyle,
    outlineWidth,
    paddingHorizontal,
    paddingVertical,
    borderRadius
  } = _ref;
  return {
    backgroundColor,
    outlineColor,
    outlineOffset,
    outlineStyle,
    outlineWidth,
    paddingHorizontal,
    paddingVertical,
    borderRadius
  };
};
const selectTextStyles = _ref2 => {
  let {
    color,
    textLine,
    fontName,
    fontSize,
    fontWeight,
    lineHeight
  } = _ref2;
  return {
    color,
    textDecorationLine: textLine,
    fontFamily: `${fontName}${fontWeight}normal`,
    lineHeight: fontSize * lineHeight,
    fontSize
  };
};

/**
 * A generic Skip link component, unstyled by default.
 * A Skip link component help keyboard-only users, screen reader users to skip
 * sections and navigate to the content they want.
 *
 * ## Component API
 *
 * For common uses, pass a `href` that is a # link to a DOM id that can be skipped to (web only).
 *
 * The element with this ID should be focusable, e.g. `<Box nativeID="skip-target" focusable>`.
 *
 * Other custom behaviour may be set by passing an `onPress` function, and routers may be integrated
 * in the same way as other navigation-related components by passing a `LinkRouter`; but a # anchor
 * href on web and/or a `targetRef` for cross-platform applications is the recommended approach.
 *
 * ## Visible styling
 *
 * When focused, the skip link shows as a visible element similar to a simplified ButtonLink using
 * UDS theming. The `tokens` prop may be used to override these styles.
 *
 * To control the background of a skip link, the following tokens can be used:
 *
 * - `backgroundColor`
 * *
 * In order to control the color of the skip link text, the following tokens can be used:
 *
 * - `color`
 *
 * ### Padding
 *
 * The following padding tokens can be used:
 *
 * - `paddingHorizontal`
 * - `paddingVertical`
 *
 * ### Outline
 *
 * The following tokens to control the outline:
 *
 * - `outlineColor`
 * - `outlineOffset`
 * - `outlineStyle`
 * - `outlineWidth`
 *
 * ## Usability and A11y guidelines
 *
 * - The skip link component is visually hidden until a keyboard press activates it.
 * - Usually, you should place the skip link immediately after the opening <body> tag.
 * - This lets users bypass top-level navigation links and jump to the main content on a page.
 * - Also consider using SkipLink before a complex feature containing many focusable elements.
 *
 * ## Accessibility
 *
 * Skip link supports all the common a11y and link props.
 */
const SkipLink = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    tokens,
    variant,
    href,
    children,
    ...rawRest
  } = _ref3;
  const {
    onPress,
    ...rest
  } = _utils.clickProps.toPressProps(rawRest);
  const getTokens = (0, _ThemeProvider.useThemeTokensCallback)('SkipLink', tokens, variant);
  const handlePress = event => {
    if (typeof onPress === 'function') onPress(event);
    // TODO - support native apps with something based on refs and/or setAccessibilityFocus
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Pressable.default, {
    ref: ref,
    accessibilityRole: "link",
    onPress: handlePress,
    href: href,
    style: pressableState => {
      const themeTokens = (0, _utils.resolvePressableTokens)(getTokens, pressableState);
      const skipLinkStyle = selectStyles(themeTokens);
      const {
        focused
      } = pressableState;
      return [staticStyles.absolute, skipLinkStyle, !focused && staticStyles.hidden];
    },
    ...selectProps(rest),
    children: pressableState => {
      const themeTokens = (0, _utils.resolvePressableTokens)(getTokens, pressableState);
      const textStyles = selectTextStyles(themeTokens);
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
        style: [textStyles, staticStyles.baseline],
        children: children
      });
    }
  });
});
SkipLink.displayName = 'SkipLink';
SkipLink.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * The text content shown or read out when the SkipLink is focused, usually a string.
   */
  children: _propTypes.default.node,
  /**
   * The target to skip to. Usually an anchor link to a section id (e.g. href="#main-section").
   */
  href: _propTypes.default.string,
  tokens: (0, _utils.getTokensPropType)('SkipLink'),
  variant: _utils.variantProp.propType
};
const staticStyles = _StyleSheet.default.create({
  baseline: {
    alignSelf: 'baseline'
  },
  absolute: {
    margin: 0,
    position: 'absolute',
    top: 0,
    left: 0
  },
  hidden: {
    overflow: 'hidden',
    ..._Platform.default.select({
      web: {
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)'
      },
      default: {
        // width / height of 0 would make it non-focusable
        height: 1,
        width: 1,
        opacity: 0
      }
    })
  }
});
var _default = exports.default = (0, _utils.withLinkRouter)(SkipLink);